import axios from 'axios'
import { message } from 'antd'
import { API_URL } from '../config/runtimeConfig'

const IS_DEV = typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production'

// Create axios instance
const api = axios.create({
  baseURL: API_URL || undefined,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Keep-alive and reconnection management
let isOnline = navigator.onLine
let retryQueue = []
let keepAliveInterval = null
let isRefreshingToken = false
let refreshQueue = []
let redirectingToLogin = false

const settleRefreshQueue = (error, token = null) => {
  refreshQueue.forEach(({ resolve, reject }) => error ? reject(error) : resolve(token))
  refreshQueue = []
}

const clearSessionAndRedirect = () => {
  localStorage.removeItem('token')
  localStorage.removeItem('refreshToken')
  delete api.defaults.headers.common['Authorization']
  stopKeepAlive()
  if (!redirectingToLogin && window.location.pathname !== '/login') {
    redirectingToLogin = true
    message.warning('เซสชันหมดอายุ โปรดเข้าสู่ระบบใหม่')
    window.location.assign('/login')
  }
}

// Start keep-alive mechanism
const startKeepAlive = () => {
  if (keepAliveInterval) return

  // Send ping every 5 minutes to keep connection alive
  keepAliveInterval = setInterval(async () => {
    if (!isOnline || document.hidden) return

    try {
      await api.get('/health-check', { timeout: 5000 })
    } catch (error) {
      console.log('Keep-alive ping failed:', error.message)
    }
  }, 5 * 60 * 1000) // 5 minutes
}

// Stop keep-alive
const stopKeepAlive = () => {
  if (keepAliveInterval) {
    clearInterval(keepAliveInterval)
    keepAliveInterval = null
  }
}

// Handle online/offline events
window.addEventListener('online', () => {
  isOnline = true
  message.success('การเชื่อมต่ออินเทอร์เน็ตกลับมาแล้ว')

  // Retry failed requests
  while (retryQueue.length > 0) {
    const { resolve, reject, config } = retryQueue.shift()
    api.request(config).then(resolve).catch(reject)
  }

  startKeepAlive()
})

window.addEventListener('offline', () => {
  isOnline = false
  message.warning('การเชื่อมต่ออินเทอร์เน็ตขาดหาย')
  stopKeepAlive()
})

// Handle page visibility changes (tab sleep mode)
document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    stopKeepAlive()
  } else {
    // When tab becomes active again, verify token and restart keep-alive
    const token = localStorage.getItem('token')
    if (token && isOnline) {
      startKeepAlive()

      // Verify token is still valid
      setTimeout(async () => {
        try {
          await api.get('/api/auth/me', { timeout: 10000 })
        } catch (error) {
          if (error.response?.status === 401) {
            console.log('Token expired after tab sleep, refreshing page...')
            // Force refresh if token expired
            window.location.reload()
          }
        }
      }, 1000)
    }
  }
})

// Initialize keep-alive if user is logged in
if (localStorage.getItem('token')) {
  startKeepAlive()
}

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add auth token to requests
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Log request in development
    // if (process.env.DEV) {
    //   console.log('API Request:', {
    //     method: config.method?.toUpperCase(),
    //     url: config.url,
    //     data: config.data,
    //     params: config.params
    //   })
    // }

    return config
  },
  (error) => {
    console.error('Request error:', error)
    return Promise.reject(error)
  }
)

// Response interceptor with retry logic
api.interceptors.response.use(
  (response) => {
    if (!['get', 'head', 'options'].includes(String(response.config?.method || 'get').toLowerCase())) {
      window.dispatchEvent(new Event('erp:financial-changed'));
      if (typeof BroadcastChannel === 'function') {
        const channel = new BroadcastChannel('erp-financial'); channel.postMessage('changed'); channel.close();
      }
    }

    // Log response in development
    // if (process.env.DEV) {
    //   console.log('API Response:', {
    //     status: response.status,
    //     url: response.config.url,
    //     data: response.data
    //   })
    // }

    return response
  },
  async (error) => {
    const originalRequest = error.config

    // Log error in development
    if (IS_DEV) {
      
      console.error('API Error:', {
        status: error.response?.status,
        url: error.config?.url,
        message: error.response?.data?.message || error.message,
        data: error.response?.data
      })
    }

    // Handle network errors with retry logic
    if (!error.response && !originalRequest._retry) {
      originalRequest._retry = true

      if (!isOnline) {
        // Queue request for when online
        return new Promise((resolve, reject) => {
          retryQueue.push({ resolve, reject, config: originalRequest })
        })
      }

      // Retry after a short delay
      await new Promise(resolve => setTimeout(resolve, 2000))
      return api.request(originalRequest)
    }

    // Handle specific error statuses
    if (error.response?.status === 401) {
      // Installation endpoints use a one-time setup token, not the normal JWT
      // refresh flow. Keep validation errors inside the installer wizard.
      if (originalRequest?.url?.includes('/api/installation/')) return Promise.reject(error)
      const refreshToken = localStorage.getItem('refreshToken')
      const isRefreshRequest = originalRequest?.url?.includes('/api/auth/refresh')

      if (!originalRequest || originalRequest._retry || isRefreshRequest || !refreshToken) {
        clearSessionAndRedirect()
        return Promise.reject(error)
      }

      originalRequest._retry = true

      if (isRefreshingToken) {
        return new Promise((resolve, reject) => refreshQueue.push({ resolve, reject }))
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            return api.request(originalRequest)
          })
      }

      isRefreshingToken = true
      try {
        const refreshResponse = await axios.post(`${API_URL || ''}/api/auth/refresh`, { refreshToken }, {
          headers: { 'Content-Type': 'application/json' },
          timeout: 30000
        })
        const data = refreshResponse.data?.data || refreshResponse.data || {}
        const newToken = data.token || refreshResponse.data?.accessToken
        const newRefreshToken = data.refreshToken || refreshResponse.data?.refreshToken
        if (!newToken || !newRefreshToken) throw new Error('Invalid token refresh response')

        localStorage.setItem('token', newToken)
        localStorage.setItem('refreshToken', newRefreshToken)
        api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`
        settleRefreshQueue(null, newToken)
        originalRequest.headers.Authorization = `Bearer ${newToken}`
        return api.request(originalRequest)
      } catch (refreshError) {
        settleRefreshQueue(refreshError)
        clearSessionAndRedirect()
        return Promise.reject(refreshError)
      } finally {
        isRefreshingToken = false
      }
    } else if (error.response?.status === 403) {
      // Forbidden - insufficient permissions
      // message.error('ไม่มีสิทธิ์ในการทำงานนี้')
    } else if (error.response?.status === 404) {
      // Not found
      message.error('ไม่พบข้อมูลที่ต้องการ')
    } else if (error.response?.status === 422) {
      // Validation error - usually handled by the calling component
      // Don't show generic error message
    } else if (error.response?.status >= 500) {
      // Server error
      //message.error('เกิดข้อผิดพลาดเซิร์ฟเวอร์ โปรดลองใหม่อีกครั้ง')
    } else if (error.code === 'ECONNABORTED') {
      // Timeout
      //message.error('การเชื่อมต่อข้อมูลล้าช้า โปรดตรวจสอบการเชื่อมอินเทอร์เน็ต')
    } else if (!error.response) {
      // Network error
      if (isOnline) {
        // message.error('ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ โปรดตรวจสอบการเชื่อมอินเทอร์เน็ต')
      }
    }

    return Promise.reject(error)
  }
)

export default api

// API helper functions
export const apiHelpers = {
  // GET request with error handling
  async get(url, config = {}) {
    try {
      const response = await api.get(url, config)
      return { success: true, data: response.data, response }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data || error.message,
        status: error.response?.status
      }
    }
  },

  // POST request with error handling
  async post(url, data = {}, config = {}) {
    try {
      const response = await api.post(url, data, config)
      return { success: true, data: response.data, response }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data || error.message,
        status: error.response?.status
      }
    }
  },

  // PUT request with error handling
  async put(url, data = {}, config = {}) {
    try {
      const response = await api.put(url, data, config)
      return { success: true, data: response.data, response }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data || error.message,
        status: error.response?.status
      }
    }
  },

  // DELETE request with error handling
  async delete(url, config = {}) {
    try {
      const response = await api.delete(url, config)
      return { success: true, data: response.data, response }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data || error.message,
        status: error.response?.status
      }
    }
  },

  // Upload file with progress
  async upload(url, formData, onProgress = null) {
    try {
      const response = await api.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: onProgress ? (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          )
          onProgress(percentCompleted)
        } : undefined,
      })
      return { success: true, data: response.data, response }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data || error.message,
        status: error.response?.status
      }
    }
  }
}
